import { t } from 'i18next';
import { FilterConfiguration } from '../../components/FilterPanel/types';
import { TypographyOverflow } from '../../components/TypographyOverflow';
import { PaginatedTableController, PaginatedTableState } from '../../controllers/PaginatedTableController';
import { FieldConfiguration } from '../../types/paginated-table';
import { BLANK_PAGINATION_RESULT, CursorInfo, PaginationResult } from '../../types/pagination';
import { DateUtils } from '../../utils/DateUtils';
import { formatTlsServerVersion } from '../enums/TlsServerVersion';
import { TlsHandshake } from '../models/TlsHandshake';

export type Props = {};

type State = PaginatedTableState & {};

export class TlsHandshakesTableController<P extends Props = Props> extends PaginatedTableController<TlsHandshake, P, State> {
  protected async fetchData(_: CursorInfo): Promise<PaginationResult<TlsHandshake>> {
    return BLANK_PAGINATION_RESULT;
  }

  protected enableSorting(): boolean {
    return true;
  }

  protected getFieldConfigurations(): FieldConfiguration<TlsHandshake>[] {
    return [
      {
        fieldKey: 'sourceIp',
        label: t('handshakes.sourceIp'),
        sortable: this.enableSorting(),
        gqlKeys: { sorterField: 'initiator_ip' },
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{item.getInitiatorIp()}</TypographyOverflow>,
      },
      {
        fieldKey: 'targetIp',
        label: t('handshakes.targetIp'),
        sortable: this.enableSorting(),
        gqlKeys: { sorterField: 'target_ip' },
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{item.getTargetIp()}</TypographyOverflow>,
      },
      {
        fieldKey: 'sourcePort',
        label: t('handshakes.sourcePort'),
        sortable: this.enableSorting(),
        gqlKeys: { sorterField: 'initiator_port' },
        columnProps: { maxWidth: 150 },
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{item.getInitiatorPort()}</TypographyOverflow>,
      },
      {
        fieldKey: 'targetPort',
        label: t('handshakes.targetPort'),
        sortable: this.enableSorting(),
        gqlKeys: { sorterField: 'target_port' },
        columnProps: { maxWidth: 150 },
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{item.getTargetPort()}</TypographyOverflow>,
      },
      {
        fieldKey: 'timestamp',
        label: t('handshakes.timestamp'),
        sortable: this.enableSorting(),
        gqlKeys: { sorterField: 'timestamp' },
        columnProps: { minWidth: 230 },
        render: (item, { hasFocus }) => {
          const timestamp = item.getTimestamp();
          if (timestamp) {
            return <TypographyOverflow hasFocus={hasFocus}>{DateUtils.toTimestampString(timestamp)}</TypographyOverflow>;
          }
          return t('handshakes.emptyDate');
        },
      },
      {
        fieldKey: 'serverName',
        label: t('handshakes.serverName'),
        sortable: this.enableSorting(),
        gqlKeys: { sorterField: 'server_name_indication' },
        render: (item, { hasFocus }) => {
          const server = item.getServerName();
          if (server) {
            return <TypographyOverflow hasFocus={hasFocus}>{server}</TypographyOverflow>;
          }
          return t('handshakes.emptyCol');
        },
      },
      {
        fieldKey: 'ciphersuite',
        label: t('handshakes.ciphersuite'),
        columnProps: { minWidth: 200 },
        sortable: false,
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{item.getSelectedCiphersuiteName()}</TypographyOverflow>,
      },
      {
        fieldKey: 'version',
        label: t('handshakes.version'),
        sortable: this.enableSorting(),
        gqlKeys: { sorterField: 'server_chosen_version' },
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{formatTlsServerVersion(item.getServerChosenVersion())}</TypographyOverflow>,
      },
    ];
  }

  protected getFilterConfigurations(): FilterConfiguration[] {
    return [];
  }
}
